import {
  useCallback,
  useEffect,
  type KeyboardEvent,
  type MouseEvent,
  useState,
} from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { syncChallengeEvent } from "../../challenges/services/challengeAdminService";
import type { ChallengeHistoryItem } from "../../challenges/types/challengeAdmin";
import {
  getEventPaymentSettings,
  updateEventPaymentSettings,
} from "../services/asaasAdminService";
import {
  getEventChallengeLinkSummary,
  type AdminEventChallengeLink,
} from "../services/eventRegistrationAdminService";
import { resolveChallengeEventId } from "../utils/adminChallengeEditor";

type ChallengeRegistrationToggleItem = Pick<
  ChallengeHistoryItem,
  "id" | "challengeType" | "eventId"
> &
  Partial<
    Pick<
      ChallengeHistoryItem,
      "tournamentBracketsCount" | "tournamentBracketMatchesCount"
    >
  >;

function hasGeneratedTournamentBrackets(item: ChallengeRegistrationToggleItem) {
  return (
    Number(item.tournamentBracketsCount || 0) > 0 ||
    Number(item.tournamentBracketMatchesCount || 0) > 0
  );
}

type UseAdminChallengeRegistrationTogglesInput = {
  eventChallengeLinks: Record<string, AdminEventChallengeLink>;
  filteredHistory: ChallengeHistoryItem[];
  isAdmin: boolean;
  isOrganizer: boolean;
  loadEventLinks: () => Promise<void>;
  onRegistrationStatusChange?: (input: {
    eventId: string;
    registrationsOpen: boolean;
    tournamentId: number;
  }) => void;
  showErrorToast: (message: string) => void;
  showSuccessToast: (message: string) => void;
  token?: string | null;
};

export function useAdminChallengeRegistrationToggles({
  eventChallengeLinks,
  filteredHistory,
  isAdmin,
  isOrganizer,
  loadEventLinks,
  onRegistrationStatusChange,
  showErrorToast,
  showSuccessToast,
  token,
}: UseAdminChallengeRegistrationTogglesInput) {
  const t = siteTexts.admin;
  const [
    challengeRegistrationsOpenByEventId,
    setChallengeRegistrationsOpenByEventId,
  ] = useState<Record<string, boolean>>({});
  const [
    challengeRegistrationsToggleLoadingByEventId,
    setChallengeRegistrationsToggleLoadingByEventId,
  ] = useState<Record<string, boolean>>({});

  const onToggleChallengeRegistrationsFromList = useCallback(
    async (
      item: ChallengeRegistrationToggleItem,
      event?: MouseEvent | KeyboardEvent,
    ) => {
      event?.stopPropagation();
      if (!token || (!isAdmin && !isOrganizer)) return;
      const eventId = resolveChallengeEventId(item);
      if (!eventId) {
        showErrorToast(t.eventLink.toasts.eventNotFound);
        return;
      }
      const hasLinkedEvent = Object.values(eventChallengeLinks).some(
        (entry) => Number(entry.tournamentId) === Number(item.id),
      );
      setChallengeRegistrationsToggleLoadingByEventId((prev) => ({
        ...prev,
        [eventId]: true,
      }));
      try {
        const currentKnown = challengeRegistrationsOpenByEventId[eventId];
        const current =
          typeof currentKnown === "boolean"
            ? currentKnown
            : hasLinkedEvent
              ? Boolean(
                  isAdmin
                    ? (await getEventPaymentSettings(token, eventId))
                        .registrationsOpen
                    : (await getEventChallengeLinkSummary(eventId))
                        .registrationsOpen,
                )
              : false;
        const next = !current;
        if (next && hasGeneratedTournamentBrackets(item)) {
          showErrorToast(
            "As chaves já foram geradas. Não é possível reabrir as inscrições.",
          );
          return;
        }
        setChallengeRegistrationsOpenByEventId((prev) => ({
          ...prev,
          [eventId]: next,
        }));
        onRegistrationStatusChange?.({
          eventId,
          registrationsOpen: next,
          tournamentId: Number(item.id),
        });
        await syncChallengeEvent(token, Number(item.id), {
          enabled: true,
          registrationType:
            item.challengeType === "poco" ? "fixed_pair" : "individual",
          registrationsOpen: next,
        });

        if (isAdmin) {
          await updateEventPaymentSettings(token, eventId, {
            registrationsOpen: next,
          });
          await loadEventLinks();
        }
        setChallengeRegistrationsOpenByEventId((prev) => ({
          ...prev,
          [eventId]: next,
        }));
        onRegistrationStatusChange?.({
          eventId,
          registrationsOpen: next,
          tournamentId: Number(item.id),
        });
        showSuccessToast(
          next
            ? t.eventLink.toasts.registrationsOpened
            : t.eventLink.toasts.registrationsClosed,
        );
      } catch (err) {
        if (eventId) {
          setChallengeRegistrationsOpenByEventId((prev) => {
            const next = { ...prev };
            delete next[eventId];
            return next;
          });
        }
        showErrorToast(
          err instanceof Error
            ? err.message
            : t.eventLink.toasts.registrationsUpdateError,
        );
      } finally {
        setChallengeRegistrationsToggleLoadingByEventId((prev) => ({
          ...prev,
          [eventId]: false,
        }));
      }
    },
    [
      challengeRegistrationsOpenByEventId,
      eventChallengeLinks,
      isAdmin,
      isOrganizer,
      loadEventLinks,
      onRegistrationStatusChange,
      setChallengeRegistrationsOpenByEventId,
      setChallengeRegistrationsToggleLoadingByEventId,
      showErrorToast,
      showSuccessToast,
      t.eventLink.toasts.eventNotFound,
      t.eventLink.toasts.registrationsClosed,
      t.eventLink.toasts.registrationsOpened,
      t.eventLink.toasts.registrationsUpdateError,
      token,
    ],
  );

  useEffect(() => {
    if (!token || (!isAdmin && !isOrganizer)) return;
    const eventIdsToLoad = filteredHistory
      .slice(0, 24)
      .filter(
        (item) =>
          String(item.status || "")
            .trim()
            .toLowerCase() === "pending",
      )
      .map((item) => resolveChallengeEventId(item))
      .filter((eventId) => Boolean(eventId))
      .filter(
        (eventId) => challengeRegistrationsOpenByEventId[eventId] === undefined,
      );
    if (eventIdsToLoad.length === 0) return;

    let cancelled = false;
    void Promise.all(
      eventIdsToLoad.map(async (eventId) => {
        try {
          const registrationsOpen = isAdmin
            ? Boolean(
                (await getEventPaymentSettings(token, eventId))
                  .registrationsOpen,
              )
            : Boolean(
                (await getEventChallengeLinkSummary(eventId)).registrationsOpen,
              );
          return {
            eventId,
            registrationsOpen,
          };
        } catch (_err) {
          return null;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setChallengeRegistrationsOpenByEventId((prev) => {
        let changed = false;
        const next = { ...prev };
        entries.forEach((entry) => {
          if (!entry) return;
          if (next[entry.eventId] === entry.registrationsOpen) return;
          next[entry.eventId] = entry.registrationsOpen;
          changed = true;
        });
        return changed ? next : prev;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [
    challengeRegistrationsOpenByEventId,
    filteredHistory,
    isAdmin,
    isOrganizer,
    setChallengeRegistrationsOpenByEventId,
    token,
  ]);

  return {
    challengeRegistrationsOpenByEventId,
    challengeRegistrationsToggleLoadingByEventId,
    onToggleChallengeRegistrationsFromList,
  };
}
